import { useEffect, useState, type ReactNode } from 'react';
import { Check, Hourglass, ListTodo, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  TASK_AI_BUILDER_NAME,
  type TaskMember,
  type TaskSearchHit,
  type TaskWaiting,
} from '@shared/types/tasks';
import { formatTaskNumber } from '@shared/task-number';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { Spinner } from '@/components/ui/spinner';
import { LoadError } from '@/components/LoadError';
import { searchTasks } from '@/lib/api/omni-search';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { cn } from '@/lib/utils';
import { AiBuilderAvatar, memberHandle, memberName } from './task-chips';
import { taskPerson } from './task-people';
import { waitingKey } from './task-waiting';

/**
 * Waiting on (T-331): whose turn a task is. People, other tasks (until they're done) and the AI
 * builder, beside the assignees, who keep the task. Waits never move it between columns.
 */

/** Toggle who it's waiting on: the AI builder (when the team has it), people, or a task found by number or title. */
export function WaitingPicker({
  members,
  aiBuilder,
  selected,
  taskId,
  onChange,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  /** Open as soon as it appears (picked from the properties' + menu). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  members: TaskMember[];
  aiBuilder: boolean;
  selected: TaskWaiting[];
  /** The task itself, which can't wait on itself. */
  taskId: string;
  onChange: (next: TaskWaiting[]) => void;
  children: ReactNode;
}) {
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 250);
  // Results remember the query they answer, so a stale answer never shows.
  const [result, setResult] = useState<{ query: string; hits: TaskSearchHit[]; failed?: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const searching = query.length >= 1;
  const loading = searching && result?.query !== query;
  const failed = searching && result?.query === query && result.failed;
  const hits = searching && result?.query === query ? result.hits.filter((h) => h.id !== taskId && !h.is_done) : [];

  useEffect(() => {
    if (!searching) return undefined;
    let cancelled = false;
    searchTasks(query, 6, { throwOnError: true }).then(
      (results) => {
        if (!cancelled) setResult({ query, hits: results });
      },
      () => {
        if (!cancelled) setResult({ query, hits: [], failed: true });
      }
    );
    return () => {
      cancelled = true;
    };
  }, [query, searching, attempt]);

  const keys = new Set(selected.map(waitingKey));
  const toggle = (item: TaskWaiting) => {
    const key = waitingKey(item);
    onChange(keys.has(key) ? selected.filter((w) => waitingKey(w) !== key) : [...selected, item]);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search people or tasks…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{loading ? null : 'No one found.'}</CommandEmpty>
            {aiBuilder && (!search || TASK_AI_BUILDER_NAME.toLowerCase().includes(search.trim().toLowerCase())) ? (
              <CommandGroup>
                <CommandItem value="ai" onSelect={() => toggle({ target: 'ai' })}>
                  <AiBuilderAvatar />
                  <span className="flex-1">{TASK_AI_BUILDER_NAME}</span>
                  {keys.has('ai') ? <Check className="size-4" /> : null}
                </CommandItem>
              </CommandGroup>
            ) : null}
            <CommandGroup heading="People">
              {members
                .filter((m) => !search.trim() || `${memberName(m)} ${m.email} ${m.handle ?? ''}`.toLowerCase().includes(search.trim().toLowerCase()))
                .map((m) => (
                  <CommandItem key={m.user_id} value={`user:${m.user_id}`} onSelect={() => toggle({ target: 'user', user_id: m.user_id })}>
                    <MemberAvatar name={memberName(m)} photourl={m.photourl} size="sm" hint={false} />
                    <span className="flex-1 truncate">{memberName(m)}</span>
                    {keys.has(`user:${m.user_id}`) ? <Check className="size-4" /> : null}
                  </CommandItem>
                ))}
            </CommandGroup>
            {searching ? (
              <CommandGroup heading="Tasks">
                {loading ? (
                  <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
                    <Spinner size="xs" />
                    Searching tasks…
                  </div>
                ) : failed ? (
                  <div className="px-2 py-1">
                    <LoadError compact title="Couldn't search tasks" onRetry={() => setAttempt((n) => n + 1)} />
                  </div>
                ) : (
                  hits.map((h) => (
                    <CommandItem
                      key={h.id}
                      value={`task:${h.id}`}
                      onSelect={() => toggle({ target: 'task', task_id: h.id, number: h.number, title: h.title, done: h.is_done })}
                    >
                      <ListTodo className="size-4 text-muted-foreground" />
                      <span className="flex-1 truncate">
                        <span className="mr-1 text-xs tabular-nums text-muted-foreground">{formatTaskNumber(h.number)}</span>
                        {h.title}
                      </span>
                      {keys.has(`task:${h.id}`) ? <Check className="size-4" /> : null}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** One wait as a chip in the task's properties, with an X to stop waiting. */
export function WaitingChip({
  waiting,
  members,
  onRemove,
  onNavigate,
  className,
}: {
  waiting: TaskWaiting;
  members: TaskMember[];
  onRemove?: () => void;
  onNavigate?: () => void;
  className?: string;
}) {
  let face: ReactNode;
  let label: ReactNode;
  let name: string;
  if (waiting.target === 'user') {
    const member = taskPerson(members, waiting.user_id);
    name = memberName(member);
    face = <MemberAvatar name={name} photourl={member?.photourl} size="sm" hint={false} />;
    label = <span className="max-w-[10rem] truncate">{memberHandle(member)}</span>;
  } else if (waiting.target === 'task') {
    name = `${formatTaskNumber(waiting.number)} ${waiting.title}`;
    face = <ListTodo className="mx-1 size-4 text-muted-foreground" aria-hidden />;
    label = (
      <Link to={`/tasks/${waiting.task_id}`} onClick={onNavigate} className={cn('max-w-[12rem] truncate hover:underline', waiting.done && 'line-through')}>
        {formatTaskNumber(waiting.number)} {waiting.title}
      </Link>
    );
  } else {
    name = TASK_AI_BUILDER_NAME;
    face = <AiBuilderAvatar />;
    label = <span>{TASK_AI_BUILDER_NAME}</span>;
  }
  return (
    <span data-hint={`Waiting on ${name}`} className={cn('inline-flex h-7 items-center gap-1.5 rounded-full pl-0.5 pr-1 text-sm', className)}>
      {face}
      {label}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Stop waiting on ${name}`}
          className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={onRemove}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

/**
 * The card's "waiting on" faces: an hourglass, then whose turn it is. `yourTurn`: it's waiting on
 * the person looking, which reads "Your turn" instead.
 */
export function WaitingFaces({ waiting, members, yourTurn, max = 2 }: { waiting: TaskWaiting[]; members: TaskMember[]; yourTurn?: boolean; max?: number }) {
  if (!waiting.length) return null;
  if (yourTurn) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 font-medium text-warning-strong" data-hint="It’s waiting on you">
        <Hourglass className="size-3.5" aria-hidden />
        Your turn
      </span>
    );
  }
  const names = waiting.map((w) =>
    w.target === 'user' ? memberName(taskPerson(members, w.user_id)) : w.target === 'task' ? formatTaskNumber(w.number) : TASK_AI_BUILDER_NAME
  );
  const shown = waiting.slice(0, max);
  return (
    <span className="inline-flex shrink-0 items-center gap-1" data-hint={`Waiting on ${names.join(', ')}`}>
      <Hourglass className="size-3.5" aria-hidden />
      <span className="flex -space-x-1.5">
        {shown.map((w) =>
          w.target === 'user' ? (
            <MemberAvatar
              key={waitingKey(w)}
              name={memberName(taskPerson(members, w.user_id))}
              photourl={taskPerson(members, w.user_id)?.photourl}
              size="xs"
              ring
              hint={false}
            />
          ) : w.target === 'ai' ? (
            <AiBuilderAvatar key="ai" size="xs" ring />
          ) : (
            <span key={waitingKey(w)} className="relative rounded bg-muted px-1 text-3xs font-medium tabular-nums ring-2 ring-background">
              {formatTaskNumber(w.number)}
            </span>
          )
        )}
      </span>
      {waiting.length > max ? <span className="text-3xs">+{waiting.length - max}</span> : null}
    </span>
  );
}
